/**
 * Google Analytics 4 loader + page-view tracker.
 *
 * - Loads gtag.js ONLY when the user has consented (RGPD).
 * - Respects an env var `REACT_APP_GA_MEASUREMENT_ID` (format `G-XXXXXXXXXX`).
 *   If unset, the loader is a no-op so the app works without analytics.
 * - Calls `gtag('consent', 'default', {analytics_storage:'denied'})` BEFORE
 *   loading the script so Google never drops cookies until consent is granted
 *   (Google Consent Mode v2).
 */

const GA_ID = process.env.REACT_APP_GA_MEASUREMENT_ID || "";
let loaded = false;

function pushDataLayer(args) {
  // gtag pushes to window.dataLayer; we keep the exact reference Google expects.
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(args);
}

function gtag(...args) {
  pushDataLayer(args);
}

/** Initialise Consent Mode v2 with everything denied by default (RGPD). */
export function initConsentMode() {
  if (!GA_ID) return;
  if (window.__consentInitialized) return;
  window.__consentInitialized = true;
  // Google's official default: deny all storage until user consents.
  gtag("consent", "default", {
    ad_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
    analytics_storage: "denied",
    functionality_storage: "granted", // strictly necessary for site to work
    security_storage: "granted",
    wait_for_update: 500,
  });
  gtag("js", new Date());
  gtag("config", GA_ID, {
    anonymize_ip: true,
    send_page_view: false, // we send manually on route change
  });
}

/** Lazily inject gtag.js once after consent (or on first manual call). */
function ensureScriptLoaded() {
  if (loaded || !GA_ID) return;
  loaded = true;
  const s = document.createElement("script");
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;
  document.head.appendChild(s);
}

/** Update consent (called when user accepts/refuses in the banner). */
export function updateConsent(granted) {
  if (!GA_ID) return;
  gtag("consent", "update", {
    analytics_storage: granted ? "granted" : "denied",
  });
  if (granted) ensureScriptLoaded();
}

/** Track a page view (called by useAnalytics on route change). */
export function trackPageView(path, title) {
  if (!GA_ID) return;
  gtag("event", "page_view", {
    page_path: path,
    page_title: title || document.title,
    page_location: window.location.href,
  });
}

/** Track a custom event (e.g. add_to_cart, purchase, sign_up). */
export function trackEvent(name, params = {}) {
  if (!GA_ID) return;
  gtag("event", name, params);
}

export const GA_ENABLED = !!GA_ID;
